import { useMediaQuery } from '@material-ui/core'
import { Alert, Chip, Typography } from '../../../core'
import { styled } from '../../../styles'
import { BaseThemeType } from '../../../theme'
import { CheckoutFinancingPricingOption } from '../types'
import { formatCurrencyWithSymbol } from '../utils'
import FinanceMarketplacePaymentsBreakdown from './FinanceMarketplacePaymentsBreakdown'
import MarketplaceCTA from './MarketplaceCTA'

type PropTypes = {
  pricingOption: CheckoutFinancingPricingOption
}

const MainContainer = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'stretch',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.01);',
    border: `1px solid ${theme.palette.neutralGrey[400]}`,
    borderRadius: '10px',
    margin: '24px',
    position: 'relative',
  })
)

const LogoAndDescriptionContainer = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.neutralGrey[200],
    flex: 2,
    gap: isMobile ? '8px' : '24px',
    padding: isMobile ? '0px' : '16px',
    width: isMobile ? '100%' : 'auto',
  })
)
const ProductTypeChipContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  position: isMobile ? 'absolute' : 'relative',
  top: isMobile ? '-12px' : 'auto',
  left: isMobile ? '-8px' : 'auto',
}))
const LogoContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: isMobile ? '8px' : '0px',
}))
const ProductDescriptionContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: isMobile ? 'flex-start' : 'center',
  alignItems: 'center',
  width: '100%',
  padding: isMobile ? '8px' : '0px',
}))
const ProductDescription = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  textAlign: 'center',
  color: theme.palette.neutralGrey[1000],
  fontWeight: 400,
  lineHeight: '16px',
}))
const RightColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  flex: 6,
}))

const ProductDetailsAndActions = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: isMobile ? '8px' : '16px',
    gap: isMobile ? '8px' : '16px',
    backgroundColor: theme.palette.neutralGrey[100],
  })
)

const ProductDetails = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
}))

const ProductActions = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: isMobile ? 'center' : 'flex-end',
  alignItems: isMobile ? 'center' : 'flex-end',
  width: '100%',
  marginTop: isMobile ? '8px' : '0px',
}))

const RepaymentHighlight = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  marginBottom: isMobile ? '8px' : '16px',
}))

const RepaymentTitle = styled(Typography)(() => ({
  fontWeight: 600,
}))

const RepaymentNumber = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.blue[800],
  marginLeft: '8px',
}))

const ProductDetailsRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  width: '100%',
}))

const ProductDetailDisplay = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  width: '100%',
  textAlign: 'center',
}))

const ProductDetailTitle = styled(Typography)(() => ({
  lineHeight: '14px',
}))

const ProductDetailMainFigure = styled(Typography)(() => ({
  fontWeight: 600,
}))

const SpacedDivider = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    backgroundColor: theme.palette.neutralGrey[600],
    width: '2px',
    height: isMobile ? '24px' : '48px',
    marginRight: isMobile ? '8px' : '32px',
    marginLeft: isMobile ? '8px' : '32px',
  })
)

const MarketplacePricingOptionRows = (props: PropTypes) => {
  const { pricingOption } = props
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  return (
    <MainContainer isMobile={isMobile}>
      <LogoAndDescriptionContainer isMobile={isMobile}>
        <ProductTypeChipContainer isMobile={isMobile}>
          {pricingOption.sub_type_display_name && (
            <Chip color="default" variant="filled" size="small" label={pricingOption.sub_type_display_name} />
          )}
        </ProductTypeChipContainer>
        <LogoContainer isMobile={isMobile}>
          <img src={pricingOption.lender_logo_url} height="50px" alt={pricingOption.lender_display_name} />
        </LogoContainer>
        <ProductDescriptionContainer isMobile={isMobile}>
          <ProductDescription textVariant="caption2">{pricingOption.product_description}</ProductDescription>
        </ProductDescriptionContainer>
        {pricingOption?.validation_messages?.map((message, i) => (
          <Alert severity={message?.message_type === 'error' ? 'error' : 'warning'} key={i}>
            {message?.message_body}
          </Alert>
        ))}
      </LogoAndDescriptionContainer>
      <RightColumn>
        <ProductDetailsAndActions isMobile={isMobile}>
          <ProductDetails isMobile={isMobile}>
            <RepaymentHighlight isMobile={isMobile}>
              <RepaymentTitle textVariant={isMobile ? 'h6' : 'h5'}>Monthly Payment: from</RepaymentTitle>
              <RepaymentNumber textVariant="h3">
                {formatCurrencyWithSymbol(pricingOption.monthly_payment, pricingOption.currency_symbol)}
              </RepaymentNumber>
            </RepaymentHighlight>
            <ProductDetailsRow>
              <ProductDetailDisplay>
                <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>APR</ProductDetailTitle>
                <ProductDetailMainFigure textVariant={isMobile ? 'h6' : 'h5'}>
                  {pricingOption.apr}%
                </ProductDetailMainFigure>
              </ProductDetailDisplay>
              <SpacedDivider isMobile={isMobile} />
              <ProductDetailDisplay>
                <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>Total Repayable</ProductDetailTitle>
                <ProductDetailMainFigure textVariant={isMobile ? 'h6' : 'h5'}>
                  {formatCurrencyWithSymbol(pricingOption.total_repayable, pricingOption.currency_symbol)}
                </ProductDetailMainFigure>
              </ProductDetailDisplay>
              <SpacedDivider isMobile={isMobile} />
              <ProductDetailDisplay>
                <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>Financing Term</ProductDetailTitle>
                <ProductDetailMainFigure textVariant={isMobile ? 'h6' : 'h5'}>
                  {pricingOption.term}
                </ProductDetailMainFigure>
              </ProductDetailDisplay>
            </ProductDetailsRow>
          </ProductDetails>
          <ProductActions isMobile={isMobile}>
            <MarketplaceCTA pricingOption={pricingOption} />
          </ProductActions>
        </ProductDetailsAndActions>
        <FinanceMarketplacePaymentsBreakdown pricingOption={pricingOption} />
      </RightColumn>
    </MainContainer>
  )
}
export default MarketplacePricingOptionRows
